/**
 * Derived computations — values that aren't stored directly but are derived
 * from other rows in real time. Keep small functions here that any view or
 * modal can reach for.
 */
import { DB } from "./db";
import { Settings } from "./prefs";
import type { Row } from "./types";

/**
 * The current total FMV of all card lots that came from this rip
 * (anything with `Source ID` === the rip's ID). Tracks live as those cards'
 * FMVs are refreshed, and falls to zero per-card as cards get sold off.
 */
export function currentPullFMVForRip(ripId: string): number {
  if (!ripId) return 0;
  return DB.table("cards")
    .filter(c => c["Source ID"] === ripId)
    .reduce((acc, c) => acc + (Number(c["Current FMV"]) || 0), 0);
}

/**
 * "Pull FMV at rip" — live total of every pulled card lot's Current FMV
 * (cards whose Source ID points at this rip) plus any bulk-pull line totals
 * recorded on the rip. Ripped cards intentionally carry zero Unit Basis
 * because the cost was in the sealed box, so this metric tracks pull *value*,
 * not pull cost. Adding a card to a rip from the Cards tab (by setting its
 * Source ID to the rip) automatically contributes that card's Current FMV
 * to this number.
 */
export function pullBasisFMVForRip(rip: Row): number {
  const ripId = rip["Rip ID"];
  if (!ripId) return 0;
  const cardsTotal = DB.table("cards")
    .filter(c => c["Source ID"] === ripId)
    .reduce((acc, c) => acc + (Number(c["Current FMV"]) || 0), 0);
  const bulkPulls: any[] = Array.isArray(rip["Bulk Pulls"]) ? rip["Bulk Pulls"] : [];
  const bulkTotal = bulkPulls.reduce((s, b) => s + (Number(b["Line Total"]) || 0), 0);
  return +(cardsTotal + bulkTotal).toFixed(2);
}

/**
 * EV using the live pull FMV. Same formula as the stored "EV Gain/Loss" but
 * with the current value of the pulled cards instead of the value frozen at
 * rip time.
 */
export function currentEvForRip(rip: Row): number {
  const ripId = rip["Rip ID"];
  const basisRemoved = Number(rip["Basis Removed"]) || 0;
  return +(currentPullFMVForRip(ripId) - basisRemoved).toFixed(2);
}

/** True when any pull from this rip has been sold or traded out. */
export function ripHasDispositions(ripId: string): boolean {
  if (!ripId) return false;
  const ids = new Set(DB.table("cards").filter(c => c["Source ID"] === ripId).map(c => c["Card Lot ID"]));
  if (!ids.size) return false;
  for (const sale of DB.table("sales")) {
    if (String(sale["Status"] || "Completed") !== "Completed") continue;
    for (const it of (Array.isArray(sale.Items) ? sale.Items : []) as any[]) if (ids.has(it["Lot ID"])) return true;
  }
  for (const t of DB.table("trades")) {
    if (String(t["Status"] || "Accepted") !== "Accepted") continue;
    for (const o of (Array.isArray(t.Outgoing) ? t.Outgoing : []) as any[]) if (ids.has(o["Lot ID"])) return true;
  }
  return false;
}

/** Realized proceeds from a rip's pulls: sale prices on completed sales +
 *  (basis + realized gain) credited on accepted-trade outgoing lines. */
export function realizedProceedsForRip(ripId: string): number {
  if (!ripId) return 0;
  const ids = new Set(DB.table("cards").filter(c => c["Source ID"] === ripId).map(c => c["Card Lot ID"]));
  if (!ids.size) return 0;
  let total = 0;
  for (const sale of DB.table("sales")) {
    if (String(sale["Status"] || "Completed") !== "Completed") continue;
    for (const it of (Array.isArray(sale.Items) ? sale.Items : []) as any[]) {
      if (ids.has(it["Lot ID"])) total += Number(it["Sale Price"]) || 0;
    }
  }
  for (const t of DB.table("trades")) {
    if (String(t["Status"] || "Accepted") !== "Accepted") continue;
    for (const o of (Array.isArray(t.Outgoing) ? t.Outgoing : []) as any[]) {
      if (ids.has(o["Lot ID"])) total += (Number(o["Realized Gain"]) || 0) + (Number(o["Basis"]) || 0) * (Number(o["Qty"]) || 1);
    }
  }
  return +total.toFixed(2);
}

/** The honest bottom line for a rip:
 *  realized proceeds + value still held + bulk line totals \u2212 box cost. */
export function trueOutcomeForRip(rip: Row): number {
  const ripId = rip["Rip ID"];
  const bulkPulls: any[] = Array.isArray(rip["Bulk Pulls"]) ? rip["Bulk Pulls"] : [];
  const bulkTotal = bulkPulls.reduce((s, b) => s + (Number(b["Line Total"]) || 0), 0);
  return +(realizedProceedsForRip(ripId) + currentPullFMVForRip(ripId) + bulkTotal - (Number(rip["Basis Removed"]) || 0)).toFixed(2);
}

/**
 * LOCKED "Pull FMV at rip" — the value captured the moment the rip was logged
 * and stored on the rip row (`Pull FMV`). This is a permanent historical
 * snapshot: it does NOT fall when pulled cards are later sold or traded (which
 * is what made profitable boxes look like losses). For very old rips that
 * predate the stored field, we fall back to the live computation.
 */
export function lockedPullFMVForRip(rip: Row): number {
  // Until ANY pull is sold or traded, the snapshot auto-tracks the live value
  // (nightly repricing keeps refining entry-time guesses). The first
  // disposition freezes it to the stored value so realized profit stays
  // visible after pulls leave inventory. Recapture (Edit Rip) still allows a
  // deliberate post-disposition refresh.
  const ripId = rip["Rip ID"];
  if (ripId && !ripHasDispositions(ripId)) {
    return pullBasisFMVForRip(rip);
  }
  const stored = rip["Pull FMV"];
  if (stored != null && stored !== "" && Number.isFinite(Number(stored))) {
    return +(Number(stored)).toFixed(2);
  }
  return pullBasisFMVForRip(rip);
}

/**
 * Headline EV for a rip = locked pull value − basis removed. Stays put once a
 * box is profitable, regardless of later sales/trades of the pulled cards.
 */
export function lockedEvForRip(rip: Row): number {
  const basisRemoved = Number(rip["Basis Removed"]) || 0;
  return +(lockedPullFMVForRip(rip) - basisRemoved).toFixed(2);
}

/**
 * Bonus: realized FMV — sum of sale prices for any item in any Sale whose
 * Lot ID matches a card lot pulled from this rip. Useful for separating
 * "money I've already pulled out of this rip" from "value still on the shelf".
 */
export function realizedFMVForRip(ripId: string): number {
  if (!ripId) return 0;
  const pulledCardIds = new Set(
    DB.table("cards")
      .filter(c => c["Source ID"] === ripId)
      .map(c => c["Card Lot ID"])
  );
  if (!pulledCardIds.size) return 0;
  let total = 0;
  for (const sale of DB.table("sales")) {
    if (String(sale["Status"] || "Completed") !== "Completed") continue;
    const items: any[] = Array.isArray(sale.Items) ? sale.Items : [];
    for (const it of items) {
      if (pulledCardIds.has(it["Lot ID"])) {
        total += Number(it["Sale Price"]) || 0;
      }
    }
  }
  return +total.toFixed(2);
}

/* ============================================================
   Owner loan ("Due to Owner") — what the business owes the owner for
   contributed inventory + cash, less repayments.

   Contributed inventory is owed at its FMV ON THE DAY of contribution: a frozen
   snapshot stored per item (ContribFMV). It does NOT float with later price
   moves. Legacy contributions saved before this feature fall back to the item's
   Unit Basis as the owed value. Only "Owner Contribution" increases the balance
   (inventory FMV snapshot + that entry's Cash In); only "Owner Repayment"
   decreases it (its Cash Out). A profit-distribution "Owner Draw" does NOT.
   ============================================================ */

/** Owed value of a single Owner Contribution adjustment row. */
export function ownerContributionValue(txn: Row): number {
  const items: any[] = Array.isArray(txn["Items"]) ? txn["Items"] : [];
  let inv = 0;
  for (const it of items) {
    const qty = Number(it["Qty"]) || 1;
    // Frozen FMV snapshot; fall back to cost basis for pre-feature rows.
    const perUnit = (it["ContribFMV"] != null && it["ContribFMV"] !== "" && Number(it["ContribFMV"]) > 0)
      ? Number(it["ContribFMV"])
      : (Number(it["UnitBasis"]) || 0);
    inv += perUnit * qty;
  }
  const cashIn = Number(txn["Cash In"]) || 0;
  return +(inv + cashIn).toFixed(2);
}

/** Total ever contributed (inventory FMV snapshots + cash in). */
export function ownerLoanContributions(): number {
  return +DB.table("transactions")
    .filter(t => t["Type"] === "Owner Contribution" || t["Type"] === "Owner Cash Contribution")
    .reduce((acc, t) => acc + ownerContributionValue(t), 0)
    .toFixed(2);
}

/** Total repaid to the owner (Owner Repayment cash out). */
export function ownerLoanRepayments(): number {
  return +DB.table("transactions")
    .filter(t => t["Type"] === "Owner Repayment")
    .reduce((acc, t) => acc + (Number(t["Cash Out"]) || 0), 0)
    .toFixed(2);
}

/** Outstanding balance the business still owes the owner. */
export function ownerLoanBalance(): number {
  return +(ownerLoanContributions() - ownerLoanRepayments()).toFixed(2);
}

/* ============================================================
   Cash on hand — the business's running cash position.

   Nets every real cash movement onto a one-time opening balance (set in
   Configs -> Defaults). Mileage (a tax deduction, not money out of an account)
   and rips (no cash moves) are intentionally excluded. Sale proceeds are net of
   selling fees and shipping you paid; trade cash and owner adjustments are
   signed (in - out).
   ============================================================ */
export interface CashFlow {
  starting: number; salesIn: number; purchasesOut: number; expensesOut: number;
  gradingOut: number; tradesNet: number; adjustmentsNet: number; onHand: number;
}

export function cashFlowBreakdown(): CashFlow {
  const starting = Number(Settings.get().startingCashBalance) || 0;
  const salesIn = DB.table("sales").filter(s => String(s["Status"] || "Completed") === "Completed").reduce((a, s) =>
    a + ((Number(s["Subtotal"]) || 0) - (Number(s["Fees"]) || 0) - (Number(s["Shipping"]) || 0)), 0);
  const purchasesOut = DB.table("purchases").reduce((a, p) => a + (Number(p["Total Paid"]) || 0), 0);
  const expensesOut = DB.table("expenses").reduce((a, e) => a + (Number(e["Amount"]) || 0), 0);
  const gradingOut = DB.table("grading").reduce((a, g) =>
    a + (Number(g["Total Service Fees"]) || 0) + (Number(g["Total Shipping"]) || 0), 0);
  const tradesNet = DB.table("trades")
    .filter(t => String(t["Status"] || "Accepted") === "Accepted")
    .reduce((a, t) => a + (Number(t["Cash"]) || 0), 0);
  const adjustmentsNet = DB.table("transactions").reduce((a, t) =>
    a + (Number(t["Cash In"]) || 0) - (Number(t["Cash Out"]) || 0), 0);
  const onHand = +(starting + salesIn - purchasesOut - expensesOut - gradingOut + tradesNet + adjustmentsNet).toFixed(2);
  return {
    starting: +starting.toFixed(2), salesIn: +salesIn.toFixed(2),
    purchasesOut: +purchasesOut.toFixed(2), expensesOut: +expensesOut.toFixed(2),
    gradingOut: +gradingOut.toFixed(2), tradesNet: +tradesNet.toFixed(2),
    adjustmentsNet: +adjustmentsNet.toFixed(2), onHand,
  };
}

export function cashOnHand(): number { return cashFlowBreakdown().onHand; }
